import ButtonMoreMenu from "@/components/Assessments/ButtonMoreMenu";
import Question from "@/components/Assessments/Question";
import QuestionCount from "@/components/Assessments/QuestionCount";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormValues } from "@/schema/console/assessments/create/FormValues";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { AddRegular, CheckmarkRegular } from "@fluentui/react-icons";
import { Fragment } from "react";
import {
  useController,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v1 as uuid } from "uuid";

interface SubCategoryProps {
  type: "simple" | "regular";
  index: number;
  categoryIndex: number;
  onRemove: () => void;
}

function SubCategory({
  type,
  index,
  onRemove,
  categoryIndex,
}: SubCategoryProps) {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<FormValues>();

  const {
    fields: questionFields,
    remove,
    append,
    move,
  } = useFieldArray({
    control,
    name: `categories.${categoryIndex}.subcategories.${index}.questions`,
    keyName: "key",
  });

  const content = useWatch({
    control,
    name: `categories.${categoryIndex}.subcategories.${index}.content`,
  });
  const isEdit = useWatch({
    control,
    name: `categories.${categoryIndex}.subcategories.${index}.isEdit`,
  });

  const { field: contentField } = useController({
    control,
    name: `categories.${categoryIndex}.subcategories.${index}.content`,
  });

  //dnd kit
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id === over.id) return;
    const activeIndex = questionFields.findIndex((question) => {
      return question.id === active.id;
    });
    const overIndex = questionFields.findIndex(
      (question) => question.id === over.id,
    );
    move(activeIndex, overIndex);
  };

  return (
    <div className="my-card">
      <div className="flex justify-between gap-4 border-b px-5 pb-4 pt-5 text-cool-gray-700">
        <div className="flex flex-col gap-2">
          {isEdit ? (
            <Input
              {...contentField}
              autoFocus
              placeholder="Sub category name"
              // onKeyDown={(e) => {
              //   if (e.key === "Enter") {
              //     setValue(
              //       `categories.${categoryIndex}.subcategories.${index}.isEdit`,
              //       false,
              //     );
              //   }
              // }}
            />
          ) : (
            <div className="text-base font-bold">{content}</div>
          )}
          <QuestionCount
            categoryIndex={categoryIndex}
            subCategoryIndex={index}
          />
        </div>
        {isEdit ? (
          <Button
            variant="secondary"
            onClick={() =>
              setValue(
                `categories.${categoryIndex}.subcategories.${index}.isEdit`,
                false,
              )
            }
            size="icon"
          >
            <CheckmarkRegular fontSize={20} />
          </Button>
        ) : (
          <ButtonMoreMenu
            onEdit={() =>
              setValue(
                `categories.${categoryIndex}.subcategories.${index}.isEdit`,
                true,
              )
            }
            onDelete={() => onRemove()}
          />
        )}
      </div>
      <DndContext
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={questionFields}>
          {questionFields.map((question, questionIndex) => (
            <Fragment key={question.id}>
              <Question
                type={type}
                categoryIndex={categoryIndex}
                subCategoryIndex={index}
                index={questionIndex}
                onRemove={() => remove(questionIndex)}
              />
            </Fragment>
          ))}
        </SortableContext>
      </DndContext>
      <div className="px-5 py-4">
        <Button
          size="zero"
          variant="text"
          onClick={() => {
            if (type === "simple") {
              append({
                id: uuid(),
                content: "",
                isEdit: true,
                perfectScore: 0.0,
                type: "simple",
              });
            } else {
              append({
                id: uuid(),
                content: "",
                isEdit: true,
                perfectScore: 0.0,
                evaluations: [
                  {
                    id: uuid(),
                    content: "",
                    weight: 0.0,
                  },
                ],
                sdgs: [],
                type: "regular",
              });
            }
          }}
        >
          <AddRegular fontSize={20} className="mr-5 text-cool-gray-500" />
          <span className="text-cool-gray-500">{t("add-question")}</span>
        </Button>
      </div>
    </div>
  );
}

export default SubCategory;
